import {
  AgGridHamburgerMenu,
  Box,
  Button,
  ButtonVariants,
  type CareOrder,
  type ColumnDef,
  FormControlSizes,
  HStack,
  type Quote,
  ROLE,
  Tooltip,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { useFeatureFlag } from '../../../hooks';
import { useRoleAuth } from '../../../hooks/useRoleAuth';
import { CARE_ORDER_CUSTOM_COLUMN_IDS } from './careOrderColumnIds';
import {
  getCareOrderBlotterChildRows,
  isManualFillableCareOrderStatus,
  validateManualFillCareOrders,
} from './MenuItems/manualFillCareOrdersEligibility';
import { type CareOrderBlotterEntity, isCareOrderRow, isQuoteRow } from './types';

export interface UseCareOrderBlotterActionColumnParams {
  onSendRFQ: (careOrder: CareOrder) => void;
  onManualFillCareOrders: (careOrders: CareOrder[]) => void;
  onShowRFQ: (quote: Quote) => void;
  onShowTimeline: (entity: CareOrderBlotterEntity) => void;
}

export const useCareOrderBlotterActionColumn = ({
  onSendRFQ,
  onManualFillCareOrders,
  onShowRFQ,
  onShowTimeline,
}: UseCareOrderBlotterActionColumnParams) => {
  const { enableCareOrderManualFills, careOrderBlockDirectExecution } = useFeatureFlag();
  const { isRole } = useRoleAuth();
  const showManualFillAction = enableCareOrderManualFills && isRole(ROLE.TRADER);

  const column: ColumnDef<CareOrderBlotterEntity> = useMemo(() => {
    return {
      type: 'custom',
      id: CARE_ORDER_CUSTOM_COLUMN_IDS.Action,
      title: 'Actions',
      exportable: false, // make sure the column doesn't show up in exports
      width: 95,
      maxWidth: 95,
      minWidth: 95,

      params: {
        suppressColumnsToolPanel: true, // dont render as a selectable item in the column chooser
        suppressAutoSize: true,
        sortable: false,
        resizable: false,
        pinned: 'right',
        lockVisible: true,
        lockPosition: true,
        lockPinned: true,
        // The entire data object is our value in this column. We want to re-render on every data update.
        valueGetter: (params: ValueGetterParams<CareOrderBlotterEntity>): CareOrderBlotterEntity | undefined => {
          return params.data;
        },
        cellRenderer: (params: ICellRendererParams<CareOrderBlotterEntity, CareOrderBlotterEntity>) => {
          const entity = params.data;
          if (!entity) {
            return null;
          }

          const callbacks = { onShowRFQ, onSendRFQ, onManualFillCareOrders, onShowTimeline };
          const childRows = getCareOrderBlotterChildRows(params.node);
          const actionItems = getCareOrderBlotterActions(entity, childRows, {
            showManualFillAction,
            careOrderBlockDirectExecution,
          });
          const columnItem = actionItems.find(item => item.renderIn === 'column' && !item.hidden);
          const menuItems = actionItems.filter(item => item.renderIn === 'menu' && !item.hidden);

          const renderActionButton = (item: CareOrderBlotterAction, buttonProps: { flex?: string; width?: string }) => {
            const button = (
              <Button
                key={item.type}
                disabled={item.disabled}
                onClick={() => {
                  item.onClick(callbacks);
                }}
                variant={item.variant}
                size={FormControlSizes.Small}
                data-testid={item.testid}
                {...buttonProps}
              >
                {item.label}
              </Button>
            );

            if (item.disabledReason == null) {
              return button;
            }

            return (
              <Tooltip key={item.type} tooltip={item.disabledReason} targetStyle={buttonProps}>
                {button}
              </Tooltip>
            );
          };

          const renderMenuItems = () => {
            return menuItems.map(item => renderActionButton(item, { flex: '1' }));
          };

          return (
            <HStack gap="spacing6" w="100%" justifyContent="flex-start">
              <Box flex="1">{columnItem && renderActionButton(columnItem, { width: '100%' })}</Box>

              <AgGridHamburgerMenu
                renderItems={renderMenuItems}
                {...params}
                iconButtonProps={{
                  size: FormControlSizes.Small,
                  ghost: false,
                  disabled: menuItems.length === 0,
                }}
              />
            </HStack>
          );
        },
      } satisfies ColDef,
    };
  }, [
    onShowRFQ,
    onSendRFQ,
    onManualFillCareOrders,
    onShowTimeline,
    showManualFillAction,
    careOrderBlockDirectExecution,
  ]);

  return column;
};

interface CareOrderBlotterAction {
  type: 'showRFQ' | 'sendRFQ' | 'fill' | 'details';
  /** Disabled state for the action button, both in the blotter column and in the kebab menu */
  disabled: boolean;
  disabledReason?: string;
  /** Whether or not to hide the button */
  hidden: boolean;
  /** Decide if you want to render the action button in the column itself or in the kebab menu popover */
  renderIn: 'column' | 'menu';
  label: string;
  variant?: ButtonVariants;
  testid?: string;
  onClick: (params: UseCareOrderBlotterActionColumnParams) => void;
}

export function getCareOrderBlotterActions(
  entity: CareOrderBlotterEntity,
  childRows: CareOrderBlotterEntity[] = [],
  options: { showManualFillAction?: boolean; careOrderBlockDirectExecution?: boolean } = {}
): CareOrderBlotterAction[] {
  if (isCareOrderRow(entity)) {
    const isTradable = entity.isTradable({
      careOrderBlockDirectExecution: options.careOrderBlockDirectExecution === true,
    });
    const isInKindCareOrder = entity.isInKind;
    const manualFillValidation = validateManualFillCareOrders([{ careOrder: entity, childRows }]);

    return compact([
      isInKindCareOrder &&
        options.showManualFillAction === true && {
          type: 'fill',
          disabled: !manualFillValidation.manuallyFillable,
          disabledReason: manualFillValidation.reason,
          hidden: !isManualFillableCareOrderStatus(entity.OrdStatus),
          label: 'Fill',
          renderIn: 'column',
          variant: ButtonVariants.Primary,
          testid: 'care-order-blotter-action-button-fill',
          onClick: params => params.onManualFillCareOrders([entity]),
        },
      !isInKindCareOrder && {
        type: 'sendRFQ',
        disabled: !isTradable.result,
        disabledReason: isTradable.failureReason,
        hidden: entity.isComplete,
        label: 'RFQ',
        renderIn: 'column',
        variant: ButtonVariants.Primary,
        testid: 'care-order-blotter-action-button-send-rfq',
        onClick: params => params.onSendRFQ(entity),
      },
      {
        type: 'details',
        disabled: false,
        hidden: false,
        label: 'Details',
        variant: ButtonVariants.Default,
        renderIn: 'menu',
        onClick: params => params.onShowTimeline(entity),
      },
    ]);
  }

  if (isQuoteRow(entity)) {
    return [
      {
        type: 'showRFQ',
        disabled: !entity.canOpenQuote,
        hidden: entity.isTerminal,
        label: 'Show',
        renderIn: 'column',
        variant: ButtonVariants.Default,
        testid: 'care-order-blotter-action-button-show-rfq',
        onClick: params => params.onShowRFQ(entity),
      },
      {
        type: 'details',
        disabled: false,
        hidden: false,
        label: 'Details',
        variant: ButtonVariants.Default,
        renderIn: 'menu',
        onClick: params => params.onShowTimeline(entity),
      },
    ];
  }

  return [];
}
